export type FormattedInteractiveType = "Button" | "List" | "CtaUrl";

export type FormattedListItem = {
  option: string;
  description: string;
};

export type FormattedInteractive = {
  type: FormattedInteractiveType;
  message: string;
  list: FormattedListItem[];
  url?: string;
  displayText?: string;
};

const INTERACTIVE_TYPES: Record<string, FormattedInteractiveType> = {
  button: "Button",
  list: "List",
  ctaurl: "CtaUrl",
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function getIgnoreCase(obj: Record<string, unknown>, ...keys: string[]): unknown {
  const map = new Map(Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value]));
  for (const key of keys) {
    if (map.has(key.toLowerCase())) {
      return map.get(key.toLowerCase());
    }
  }
  return undefined;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function parseListItems(raw: unknown): FormattedListItem[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      const record = asRecord(item);
      if (!record) return null;
      const option = asString(getIgnoreCase(record, "Option", "option"));
      const description = asString(getIgnoreCase(record, "Description", "description"));
      if (!option && !description) return null;
      return { option, description };
    })
    .filter((item): item is FormattedListItem => item !== null);
}

export function parseFormattedText(raw: string | null | undefined): FormattedInteractive | null {
  if (!raw || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    const record = asRecord(parsed);
    if (!record) return null;

    const typeRaw = asString(getIgnoreCase(record, "Type", "type")).trim();
    const type = INTERACTIVE_TYPES[typeRaw.toLowerCase()];
    if (!type) return null;

    const message = asString(getIgnoreCase(record, "Message", "message"));
    const list = parseListItems(getIgnoreCase(record, "List", "list"));
    const url = asString(getIgnoreCase(record, "Url", "url")) || undefined;
    const displayText = asString(getIgnoreCase(record, "DisplayText", "displayText")) || undefined;

    if (type === "CtaUrl") {
      if (!displayText && !url) return null;
      return { type, message, list, url, displayText };
    }

    if (!message && list.length === 0) return null;

    return { type, message, list, url, displayText };
  } catch {
    return null;
  }
}

export function getInteractiveOptionLabel(item: FormattedListItem): string {
  return item.description.trim() || item.option.trim();
}

export type InteractiveOptionView = {
  label: string;
  subtitle?: string;
};

export function toInteractiveOptions(formatted: FormattedInteractive): InteractiveOptionView[] {
  if (formatted.type === "CtaUrl") {
    const label = (formatted.displayText || formatted.url || "").trim();
    return label ? [{ label }] : [];
  }

  if (formatted.type === "List") {
    return formatted.list
      .map((item): InteractiveOptionView | null => {
        const option = item.option.trim();
        const description = item.description.trim();
        if (!option && !description) return null;
        if (option && description && option !== description) {
          return { label: option, subtitle: description };
        }
        return { label: description || option };
      })
      .filter((item): item is InteractiveOptionView => item !== null);
  }

  return formatted.list
    .map((item) => {
      const label = getInteractiveOptionLabel(item);
      return label ? { label } : null;
    })
    .filter((item): item is InteractiveOptionView => item !== null);
}
